import * as React from 'react';
import { observer } from 'mobx-react';
import { style } from 'typestyle';
import {
  center, centerCenter, end, endJustified, horizontal, horizontallySpaced, startJustified, vertical,
  verticallySpaced
} from 'csstips';
import { em, important, percent, px, viewHeight, viewWidth } from 'csx';
import { mediaTablet, mediaMobileOnly } from '../theme/media';
import { Modal, ModalChildStyle } from './modal';
import { SVGIcon } from './svg-icon'
import { colorGoogle, colorError, colorWhite } from '../theme/color';
import { PillStyle, PillStyleDefault } from './pill';
import {ChannelSchema} from '../types/schema';

export interface ChannelAddModalModel {
  show: boolean;
  loading: boolean;
  linking: boolean;
  results: Array<ChannelSchema>;
  selection: any;
  youtubeChannelLoginURL: string;
  error: string;
}

export interface ChannelAddModalProps {
  model: ChannelAddModalModel;
  onRequestClose(): void;
  onSearch(query: string): void;
  onSelect(channel: ChannelSchema): void;
  onDeselect(): void;
  onLink(): void;
  i18n?: {
    title: string;
    titleSignIn: string;
    titleSearch: string;
    placeholder: string;
    submit: string;
    strap: string;
    select: string;
    reset: string;
    close: string;
    loading: string;
    linking: string;
    noResults: (query: string) => string;
    isLinkedTitle: string;
    isLinkedAction: string;
    notLinkedTitle: string;
    notLinkedAction: string;
  }
}

export interface ChannelAddModalState {
  query: string;
}

@observer
export class ChannelAddModal extends React.Component<ChannelAddModalProps, ChannelAddModalState> {
  constructor(props) {
    super(props);
    this.state = { query: '' };
  }

  render() {
    const { styles } = ChannelAddModal;
    const { model, i18n = ChannelAddModal.i18n, onRequestClose, onSelect, onDeselect, onLink } = this.props;
    const { query } = this.state;

    return (
      <Modal show={model.show} onRequestClose={onRequestClose}>
        {model.show && (
          <div className={styles.container}>
            <h3>{i18n.title}</h3>
            {!model.selection && (
              <div className={styles.strap}>{i18n.strap}</div>
            )}
            {!model.selection && (
              <div>
                <a className={styles.signInLink} href={model.youtubeChannelLoginURL} rel="nofollow">
                  <SVGIcon.Google />
                  <span>{i18n.notLinkedAction}</span>
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    )
  }

  handleChange = (event: React.FormEvent<any>) => {
    event.preventDefault();
    this.setState({ query: (event.target as HTMLInputElement).value });
  };

  handleSubmit = (event: React.FormEvent<any>) => {
    event.preventDefault();
    if (!this.props.model.loading) {
      this.props.onSearch(this.state.query);
    }
  };

  static i18n = {
    title: 'Add channel',
    titleSignIn: 'Sign in to add channel',
    titleSearch: 'Search for channel',
    placeholder: 'Search by YouTube channel name or ID',
    submit: 'Search for channels',
    select: 'Select',
    close: 'Close',
    reset: 'Reset selection',
    loading: 'Searching for channels...',
    strap: 'Looking today to license for a different YouTube Channel? Sign-in below with the channel you\'d like to license with.',
    noResults: (query: string) => `No channels found for "${query}"`,
    isLinkedTitle: 'This channel is on Lickd and can be added to your account.',
    isLinkedAction: 'Add to account',
    linking: 'Adding to account...',
    notLinkedTitle: 'This channel needs to be connected with Lickd in order to be added to your account.',
    notLinkedAction: 'Connect to channel'
  };

  static styles = {
    container: style(
      ModalChildStyle,
      {
        ...vertical,
        maxWidth: px(640),
        width: viewWidth(100),
        height: 'auto',
        maxHeight: percent(100),
        $nest: {
          '&>*': {
            flexShrink: 0,
          },
        }
      }
    ),
    innerContainer: style({
      flex: 1,
      flexShrink: 1,
      overflowY: 'auto',
      overflowX: 'hidden'
    }),
    signInLink: style({
      ...PillStyle,
      ...PillStyleDefault,
      padding: '0.6em',
      width: px(200),
      background: colorGoogle.toString(),
      $nest: {
        '&:hover': {
          color: colorWhite.toString(),
          background: colorGoogle.darken(0.15).toString()
        },
        '& span': {
          flex: 1,
          textAlign: 'center'
        }
      }
    }),
    strap: style({
      margin: '0 0 30px 0',
    }),
    form: style(
      { margin: '0 0 30px 0' },
      mediaMobileOnly({ ...vertical, ...verticallySpaced(10) }),
      mediaTablet({ ...horizontal, ...horizontallySpaced(10), ...center, ...startJustified }),
    ),
    error: style({
      fontSize: px(14),
      color: colorError.toString(),
    }),
    inputContainer: style(
      { flex: 1 },
      mediaMobileOnly({ width: percent(100) }),
      mediaTablet({ maxWidth: px(420) })
    ),
    input: style({
      margin: 0
    }),
    channel: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...center,
      padding: '10px 0',
      $nest: {
        '&> div:nth-child(1)': {
          width: px(60)
        },
        '&> div:nth-child(2)': {
          flex: 1,
          lineHeight: em(1.4),
        },
        '&> div:nth-child(3)': {
          width: px(110),
          textAlign: 'right'
        }
      }
    }),
    avatar: style({
      width: px(60),
      height: px(60),
    }),
    avatarLarge: style({
      width: px(120),
      height: px(120),
    }),
    selection: style({
      ...vertical,
      ...center,
      ...verticallySpaced(10),
      margin: '30px 0 0 0',
    }),
    buttons: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...endJustified,
      ...end,
      margin: '30px 0 0 0'
    })
  };
}